/**
 * `<EventCountdownHero />` - Story 17.5 shared pre-window event hero.
 *
 * Renders a vertically centered stack: optional logo, large event-name h1,
 * countdown line ("Submissions/Voting open in {N} {unit}"), absolute-time
 * line, and an optional secondary copy paragraph.
 *
 * Consumers:
 *   - `<PreLinkPage>` StepA when `phase === 'created'` (mode='submit').
 *   - `<VoterLandingPage>` `VoterLandingHero` when phase classifies as
 *     'pre-created' or 'pre-open' (mode='vote').
 *
 * Tick cadence is 30s via `setInterval` keyed on `Date.now()`. The largest
 * non-zero unit (days/hours/minutes/seconds) drives the countdown copy;
 * past targets render "starting now". Countdown line carries
 * `aria-live="off"` per AC #6 - the absolute-time line is the announceable
 * artifact.
 */
import { useEffect, useState } from 'react';
import { formatLifecycleTime } from '../lib/countdown';

const TICK_INTERVAL_MS = 30_000;
const VIEWPORT_BREAKPOINT_PX = 768;
const SECOND_MS = 1_000;
const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

// Story 18.2 — `'join'` widens the mode union for the unified
// `/e/{eventId}/join` countdown hero. Verb mapping: 'submit' →
// "Submissions open"; 'vote' → "Voting opens"; 'join' → "Event opens"
// (generic; the join page targets `submissionWindow.start` for `created`
// phase, where the next user-visible threshold is "submissions open").
export interface EventCountdownHeroProps {
  eventName: string;
  logoUrl?: string | null;
  targetIso: string;
  mode: 'submit' | 'vote' | 'join';
  secondaryCopy?: string;
  branding?: { colorPrimary?: string | null };
}

interface CountdownParts {
  startingNow: boolean;
  amount: number;
  unit: 'day' | 'hour' | 'minute' | 'second';
}

function computeCountdownParts(targetIso: string, nowMs: number): CountdownParts {
  const targetMs = new Date(targetIso).getTime();
  const deltaMs = targetMs - nowMs;
  if (deltaMs < 0) {
    return { startingNow: true, amount: 0, unit: 'second' };
  }
  if (deltaMs >= DAY_MS) {
    return { startingNow: false, amount: Math.floor(deltaMs / DAY_MS), unit: 'day' };
  }
  if (deltaMs >= HOUR_MS) {
    return { startingNow: false, amount: Math.floor(deltaMs / HOUR_MS), unit: 'hour' };
  }
  if (deltaMs >= MINUTE_MS) {
    return { startingNow: false, amount: Math.floor(deltaMs / MINUTE_MS), unit: 'minute' };
  }
  return { startingNow: false, amount: Math.floor(deltaMs / SECOND_MS), unit: 'second' };
}

function pluralizeUnit(unit: CountdownParts['unit'], amount: number): string {
  return amount === 1 ? unit : `${unit}s`;
}

function useViewportIsWide(): boolean {
  const initial =
    typeof window !== 'undefined' && window.innerWidth >= VIEWPORT_BREAKPOINT_PX;
  const [isWide, setIsWide] = useState<boolean>(initial);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => {
      setIsWide(window.innerWidth >= VIEWPORT_BREAKPOINT_PX);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);
  return isWide;
}

export function EventCountdownHero({
  eventName,
  logoUrl,
  targetIso,
  mode,
  secondaryCopy,
  branding,
}: EventCountdownHeroProps) {
  const [nowMs, setNowMs] = useState<number>(() => Date.now());

  useEffect(() => {
    const handle = window.setInterval(() => {
      setNowMs(Date.now());
    }, TICK_INTERVAL_MS);
    return () => {
      window.clearInterval(handle);
    };
  }, []);

  const isWide = useViewportIsWide();
  const headlineFontVar = isWide
    ? 'var(--text-display-lg-size)'
    : 'var(--text-display-md-size)';

  const parts = computeCountdownParts(targetIso, nowMs);
  // Story 18.2 — `'join'` reads as a neutral "Event opens" verb so it
  // works regardless of whether the next threshold is submissions or
  // voting (the join page chooses `targetIso` based on phase + windows).
  const verb =
    mode === 'submit'
      ? 'Submissions open'
      : mode === 'vote'
        ? 'Voting opens'
        : 'Event opens';
  const useBrand = Boolean(branding?.colorPrimary);
  const showLogo = typeof logoUrl === 'string' && logoUrl.length > 0;
  const absoluteTime = formatLifecycleTime(targetIso);

  return (
    <section
      data-testid="event-countdown-hero"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--space-4)',
        padding: 'var(--space-5) var(--space-3)',
        textAlign: 'center',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowWrap: 'break-word',
      }}
    >
      {showLogo ? (
        <img
          data-testid="event-countdown-hero-logo"
          src={logoUrl as string}
          alt={`${eventName} logo`}
          style={{
            maxHeight: 120,
            maxWidth: 240,
            objectFit: 'contain',
          }}
        />
      ) : null}
      <h1
        data-testid="event-countdown-hero-name"
        style={{
          margin: 0,
          fontSize: headlineFontVar,
          lineHeight: 1.05,
          fontWeight: 700,
          color: 'var(--text-primary)',
          maxWidth: '100%',
          overflowWrap: 'break-word',
        }}
      >
        {eventName}
      </h1>
      <p
        data-testid="event-countdown-hero-countdown"
        aria-live="off"
        style={{
          margin: 0,
          fontSize: 'var(--text-lg-size)',
          color: 'var(--text-primary)',
        }}
      >
        {parts.startingNow ? (
          <>{verb} starting now</>
        ) : (
          <>
            {verb} in{' '}
            <span
              data-testid="event-countdown-hero-digit"
              style={useBrand ? { color: 'var(--brand-primary)' } : undefined}
            >
              {parts.amount}
            </span>{' '}
            {pluralizeUnit(parts.unit, parts.amount)}
          </>
        )}
      </p>
      <p
        data-testid="event-countdown-hero-absolute"
        style={{
          margin: 0,
          fontSize: 'var(--text-base-size)',
          color: 'var(--text-secondary)',
        }}
      >
        {absoluteTime}
      </p>
      {secondaryCopy ? (
        <p
          data-testid="event-countdown-hero-secondary"
          style={{
            margin: 0,
            fontSize: 'var(--text-base-size)',
            color: 'var(--text-secondary)',
            maxWidth: '60ch',
          }}
        >
          {secondaryCopy}
        </p>
      ) : null}
    </section>
  );
}
